import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '@services/userService';
import { useAuth } from '@hooks/useAuth';
import toast from 'react-hot-toast';

const QUERY_KEY = 'user-profile';

export const useUser = () => {
  const queryClient = useQueryClient();
  const { user: authUser, updateUser } = useAuth();

  const {
    data: profile,
    isLoading,
    error,
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: userService.getProfile,
  });

  const updateProfileMutation = useMutation({
    mutationFn: userService.updateProfile,
    onSuccess: (data) => {
      queryClient.setQueryData([QUERY_KEY], data);
      updateUser(data);
      toast.success('Perfil atualizado com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao atualizar perfil');
    },
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: userService.updatePreferences,
    onSuccess: (data) => {
      queryClient.setQueryData([QUERY_KEY], data);
      updateUser(data);
      toast.success('Preferências salvas!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao salvar preferências');
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: userService.changePassword,
    onSuccess: () => {
      toast.success('Senha alterada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao alterar senha');
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: userService.deleteAccount,
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao excluir conta');
    },
  });

  const uploadAvatarMutation = useMutation({
    mutationFn: userService.uploadAvatar,
    onSuccess: (data) => {
      queryClient.setQueryData([QUERY_KEY], data);
      updateUser(data);
      toast.success('Foto atualizada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao atualizar foto');
    },
  });

  const sendDigestMutation = useMutation({
    mutationFn: userService.sendDigest,
    onSuccess: (data) => {
      toast.success(`Resumo enviado para ${data?.to || 'seu e-mail'}!`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao enviar resumo');
    },
  });

  return {
    profile: profile || authUser,
    isLoading,
    error,
    updateProfile: updateProfileMutation.mutateAsync,
    uploadAvatar: uploadAvatarMutation.mutateAsync,
    updatePreferences: updatePreferencesMutation.mutateAsync,
    changePassword: changePasswordMutation.mutateAsync,
    deleteAccount: deleteAccountMutation.mutateAsync,
    sendDigest: sendDigestMutation.mutateAsync,
    isUpdating: updateProfileMutation.isPending,
    isUploadingAvatar: uploadAvatarMutation.isPending,
    isSavingPrefs: updatePreferencesMutation.isPending,
    isChangingPassword: changePasswordMutation.isPending,
    isDeletingAccount: deleteAccountMutation.isPending,
    isSendingDigest: sendDigestMutation.isPending,
  };
};

export default useUser;
